import { useContext, useEffect, useState } from "react";
import { ApiTableColumn } from "../../../types/ApiTable/ApiTableColumn";
import { Form, InputGroup } from "react-bootstrap";
import styled from "styled-components";
import { TableContext } from "../Context/TableContext";
import { DateTime } from "luxon";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface FilterDateRangeProps {
  column: ApiTableColumn;
}

interface Period {
  startDate: string | undefined;
  endDate: string | undefined;
}

const Flex = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 0.25rem;
`;

export const TextboxButton = styled.button`
  background-color: var(--bs-body-bg);
  border: var(--bs-border-width) solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  border-left: none;
`;

const FilterDateRange = ({ column }: FilterDateRangeProps) => {
  const dateFormat = "yyyy-MM-dd";
  const {
    state: { filters },
    updateFilter,
    clearFilter,
  } = useContext(TableContext)!;
  const [currentFilterValue, setCurrentFilterValue] = useState(
    filters?.find((f) => f.field === column.field),
  );
  const [period, setPeriod] = useState<Period>({
    startDate: currentFilterValue?.value?.from,
    endDate: currentFilterValue?.value?.to,
  });

  const getFilterServiceText = (column: ApiTableColumn, period: Period) => {
    let expression = "";
    if (period.startDate) {
      const from = DateTime.fromFormat(period.startDate, dateFormat);
      expression += `${column.filter?.filterKey ?? column.field} ge ${from
        .toUTC()
        .toISO({ includeOffset: false })}`;
    }
    if (period.endDate) {
      const to = DateTime.fromFormat(period.endDate, dateFormat);
      expression +=
        (expression ? " _And " : "") +
        `${column.filter?.filterKey ?? column.field} le ${to
          .toUTC()
          .toISO({ includeOffset: false })}`;
    }
    return expression;
  };

  const getFilterText = (period: Period) => {
    if (period.startDate && period.endDate)
      return `between ${DateTime.fromFormat(
        period.startDate,
        dateFormat,
      ).toFormat("dd/MM/yyyy")} and ${DateTime.fromFormat(
        period.endDate,
        dateFormat,
      ).toFormat("dd/MM/yyyy")}`;
    else if (period.startDate && !period.endDate)
      return `after ${DateTime.fromFormat(
        period.startDate,
        dateFormat,
      ).toFormat("dd/MM/yyyy")}`;
    else if (!period.startDate && period.endDate)
      return `before ${DateTime.fromFormat(period.endDate, dateFormat).toFormat(
        "dd/MM/yyyy",
      )}`;
  };

  useEffect(() => {
    setCurrentFilterValue(filters?.find((f) => f.field === column.field));
  }, [filters, column]);

  useEffect(() => {
    if (
      period.startDate === currentFilterValue?.value?.from &&
      period.endDate === currentFilterValue?.value?.to
    )
      return;

    setPeriod({
      startDate: currentFilterValue?.value?.from ?? "",
      endDate: currentFilterValue?.value?.to ?? "",
    });
  }, [currentFilterValue?.value]);

  useEffect(() => {
    if (
      period.startDate === currentFilterValue?.value?.from &&
      period.endDate === currentFilterValue?.value?.to
    )
      return;

    if (!period.startDate && !period.endDate) {
      clearFilter(column.field);
      setCurrentFilterValue(undefined);
    } else {
      const filter = {
        field: column.field,
        value: {
          from: period.startDate,
          to: period.endDate,
        },
        filterText: `${column.label} ${getFilterText(period)}`,
        filterServiceText: getFilterServiceText(column, period),
      };
      updateFilter(filter);
      setCurrentFilterValue(filter);
    }
  }, [period]);

  return (
    <Flex>
      <InputGroup className="w-50">
        <Form.Control
          className=" mr-1"
          type="date"
          value={period.startDate}
          onChange={(e) => setPeriod({ ...period, startDate: e.target.value })}
          name="startDate"
          style={{ borderRight: "none", paddingRight: 0 }}
        />
        <TextboxButton onClick={() => setPeriod({ ...period, startDate: "" })}>
          <FontAwesomeIcon icon={faXmark} />
        </TextboxButton>
      </InputGroup>
      <InputGroup className="w-50">
        <Form.Control
          type="date"
          value={period.endDate}
          onChange={(e) => setPeriod({ ...period, endDate: e.target.value })}
          name="endDate"
          style={{ borderRight: "none", paddingRight: 0 }}
        />
        <TextboxButton onClick={() => setPeriod({ ...period, endDate: "" })}>
          <FontAwesomeIcon icon={faXmark} />
        </TextboxButton>
      </InputGroup>
    </Flex>
  );
};

export default FilterDateRange; 